import { useEffect, useMemo, useState } from 'react';
import { KyResponse } from 'ky';
import { shallow } from 'zustand/shallow';
import { useDebouncedCallback } from 'use-debounce';
import { BrokerageUserRole } from '@lio/rule-engine';
import { QuoteGetResponse, QuotePatchRequestBody, QuoteRequestParams } from '@lio/api-types';
import { QuoteType } from '@models/quotes';
import { AutocompleteRenderInputParams, Grid, Typography } from '@mui/material';
import { Dialog } from '@components/dialog/Dialog';
import { Item } from '@components/select';
import { useSearchPaginatedBrokerages } from '@services/brokerage/useSearchPaginatedBrokerages';
import { useStore } from '@store/GlobalStore';
import Loading from '@components/Loading';
import { Autocomplete } from '@components/autocomplete/Autocomplete';
import { TextField } from '@components/textField/TextField';
import { handleScrollToBottom } from '@utils/HandleScrollToBottom';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { useSearchPaginatedBrokerageUsers } from '@services/brokerage/user/searchPaginatedBrokerageUsers';
import { useQuote } from '@services/quotes';
import { UnderwriterTypeaheadFilter } from '@components/table/filters/UnderwriterTypeaheadFilter';

interface IAddLinkDialog {
  showDialog: boolean;
  quote?: QuoteGetResponse;
  quoteId?: string;
  closeLinkDialog: () => void;
  onUpdateQuote: (request: QuotePatchRequestBody & QuoteRequestParams) => Promise<KyResponse>;
  isUpdateQuoteLoading: boolean;
  onShowUpdateQuoteSnackbar: ({ isUpdateQuoteError }: { isUpdateQuoteError: boolean }) => void;
}

function isValidQuoteUpdate(update: {
  brokerageId?: string;
  underwriterId?: string;
  brokerageProducerId?: string;
}): update is QuotePatchRequestBody {
  if (update.brokerageId || update.brokerageProducerId || update.underwriterId) {
    return true;
  }
  return false;
}

export const RoleManagementDialog = ({
  showDialog,
  quote,
  quoteId,
  closeLinkDialog,
  onUpdateQuote,
  isUpdateQuoteLoading,
  onShowUpdateQuoteSnackbar,
}: IAddLinkDialog) => {
  const { quote: currentQuote } = useQuote(!quote ? quoteId : undefined);
  const quoteData = quote ?? currentQuote;
  const isRenewal = quoteData?.quoteType === QuoteType.Renewal;
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const [currentBrokerage, setCurrentBrokerage] = useState<Item>({
    value: quoteData?.brokerage?.id ?? '',
    label: quoteData?.brokerage?.id ? quoteData?.brokerage.name : '',
  });
  const [currentProducer, setCurrentProducer] = useState<Item>({
    value: quoteData?.brokerageProducer?.id ?? '',
    label: quoteData?.brokerageProducer?.id
      ? `${quoteData?.brokerageProducer.user.firstName} ${quoteData?.brokerageProducer.user.lastName} - ${quoteData?.brokerageProducer.user.email}`
      : '',
  });
  const [currentUnderwriter, setCurrentUnderwriter] = useState<Item>({
    value: quoteData?.underwriter.id ?? '',
    label: `${quoteData?.underwriter.user.firstName} ${quoteData?.underwriter.user.lastName} - ${quoteData?.underwriter.user.email}`,
  });
  const [brokerageSearchbarText, setBrokerageSearchbarText] = useState<string>('');
  const [producerSearchbarText, setProducerSearchbarText] = useState<string>('');
  const [areInitialValuesLoaded, setAreInitialValuesLoaded] = useState(false);

  useEffect(() => {
    if (!areInitialValuesLoaded && quoteData) {
      setCurrentBrokerage({
        value: quoteData?.brokerage?.id ?? '',
        label: quoteData?.brokerage?.id ? quoteData?.brokerage.name : '',
      });
      setCurrentProducer({
        value: quoteData?.brokerageProducer?.id ?? '',
        label: quoteData?.brokerageProducer?.id
          ? `${quoteData?.brokerageProducer.user.firstName} ${quoteData?.brokerageProducer.user.lastName} - ${quoteData?.brokerageProducer.user.email}`
          : '',
      });
      setCurrentUnderwriter({
        value: quoteData?.underwriter.id ?? '',
        label: `${quoteData?.underwriter.user.firstName} ${quoteData?.underwriter.user.lastName} - ${quoteData?.underwriter.user.email}`,
      });
      setAreInitialValuesLoaded(true);
    }
  }, [quoteData, areInitialValuesLoaded]);

  const debouncedBrokerageSearchbarText = useDebouncedCallback((value) => {
    setBrokerageSearchbarText(value);
  }, DEBOUNCE_DELAYS.TYPING);

  const debouncedProducerSearchbarText = useDebouncedCallback((value) => {
    setProducerSearchbarText(value);
  }, DEBOUNCE_DELAYS.TYPING);

  const {
    searchBrokerage,
    isLoading: isSearchBrokeragesLoading,
    fetchNextPage: loadMoreBrokerageResults,
  } = useSearchPaginatedBrokerages(true, {
    sort: '+name',
    pageLimit: 25,
    name: brokerageSearchbarText,
  });
  const {
    brokerageUsers: producers,
    isPending: isProducersLoading,
    fetchNextPage: loadMoreProducerResults,
  } = useSearchPaginatedBrokerageUsers(currentBrokerage.value ? { brokerageId: currentBrokerage.value } : undefined, {
    pageLimit: 10,
    sort: 'user.firstName',
    role: BrokerageUserRole.Producer,
    searchText: producerSearchbarText.split('-')[0].trim(),
  });

  const allBrokerages = useMemo(
    () =>
      [...(searchBrokerage?.pages?.flatMap((page) => page.items) ?? [])].map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [searchBrokerage?.pages],
  );

  const allProducers = useMemo(
    () =>
      [...(producers?.pages?.flatMap((page) => page.items) ?? [])].map((item) => ({
        value: item.brokerageProducers?.[0]?.id,
        label: `${item.user.firstName} ${item.user.lastName} - ${item.user.email}`,
      })),
    [producers?.pages],
  );

  const handleBrokerageChange = (item: Item) => {
    setCurrentBrokerage(item);
    setCurrentProducer({ value: '', label: '' });
    setProducerSearchbarText('');
  };

  const handleProducerChange = (item: Item) => {
    setCurrentProducer(item);
  };

  const handleUpdate = async () => {
    const hasBrokerageChange = currentBrokerage?.value !== quoteData?.brokerage?.id;
    const hasBrokerageProducerChange = currentProducer?.value !== quoteData?.brokerageProducer.id;
    const hasUnderwriterChange = currentUnderwriter?.value !== quoteData?.underwriter.id;
    const updates = {
      brokerageId: hasBrokerageChange ? currentBrokerage?.value : undefined,
      brokerageProducerId: hasBrokerageChange || hasBrokerageProducerChange ? currentProducer?.value : undefined,
      underwriterId: hasUnderwriterChange ? currentUnderwriter?.value : undefined,
    };
    if (isValidQuoteUpdate(updates)) {
      const quoteDataId = quoteData?.id ?? quoteId;
      if (quoteDataId) {
        try {
          await onUpdateQuote({
            quoteId: quoteDataId,
            ...updates,
          });
          onShowUpdateQuoteSnackbar({
            isUpdateQuoteError: false,
          });
          closeLinkDialog();
        } catch (e) {
          onShowUpdateQuoteSnackbar({
            isUpdateQuoteError: true,
          });
        }
      }
    }
  };

  return (
    <Dialog
      open={showDialog}
      title={`Assigned Roles for Quote #${quoteData?.policyNumber?.policyNumber}`}
      showBorders={false}
      isCloseButtonDisabled={isUpdateQuoteLoading}
      handleCloseDialog={closeLinkDialog}
      primaryBtnText='Update Assignments'
      primaryBtnAction={handleUpdate}
      isPrimaryBtnDisabled={
        !currentBrokerage.value ||
        !currentProducer.value ||
        !currentUnderwriter?.value ||
        (quoteData?.brokerageProducer?.id === currentProducer.value &&
          quoteData?.underwriter?.id === currentUnderwriter?.value)
      }
      isPrimaryBtnLoading={isUpdateQuoteLoading}
      secondaryBtnText='Exit'
      secondaryBtnAction={closeLinkDialog}
      isSecondaryBtnDisabled={isUpdateQuoteLoading}
    >
      <Grid
        size={{ xs: 11 }}
        sx={{
          p: 2,
          margin: '0 auto',
          mt: 2,
          '.MuiFormControl-root': {
            width: '100%',
          },
        }}
      >
        {(isInternalUser && allBrokerages.length === 0) || (!isInternalUser && allProducers.length === 0) ? (
          <Loading />
        ) : (
          <>
            {isInternalUser ? (
              <Autocomplete
                id='Assigned Brokerage'
                disabled={isRenewal}
                disableClearable={!currentBrokerage?.value}
                loading={isSearchBrokeragesLoading}
                onChange={(_, value, reason) => {
                  if (reason === 'clear') {
                    handleBrokerageChange({ value: '', label: '' });
                  } else if (reason === 'selectOption') {
                    handleBrokerageChange(value as Item);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (event) {
                    handleBrokerageChange({ value: '', label: '' });
                    debouncedBrokerageSearchbarText(newInputValue);
                  }
                }}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    inputLabel='Assigned Brokerage'
                    slotProps={{
                      inputLabel: { htmlFor: 'Assigned Brokerage' },
                      input: { ...params.InputProps },
                    }}
                  />
                )}
                renderOption={(props, option) =>
                  (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
                    <li {...props} key={(option as Item)?.value}>
                      <Typography variant='bodySmall'>{(option as Item).label}</Typography>
                    </li>
                  )
                }
                options={[...allBrokerages, { value: '', label: '' }]}
                value={currentBrokerage?.label}
                isOptionEqualToValue={(option, value) => option.label === (value as unknown as string)}
                sx={{ marginBottom: '24px' }}
                slotProps={{
                  listbox: {
                    style: { maxHeight: '300px' },
                    onScroll: (e) => handleScrollToBottom(e, loadMoreBrokerageResults),
                  },
                }}
              />
            ) : null}
            <Autocomplete
              id='Assigned Producer'
              disabled={!currentBrokerage?.value}
              disableClearable={!currentProducer.value}
              loading={isProducersLoading}
              onChange={(_, value, reason) => {
                if (reason === 'clear') {
                  handleProducerChange({ value: '', label: '' });
                } else if (reason === 'selectOption') {
                  handleProducerChange(value as Item);
                }
              }}
              onInputChange={(event, newInputValue) => {
                if (event) {
                  handleProducerChange({ value: '', label: '' });
                  debouncedProducerSearchbarText(newInputValue);
                }
              }}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                  {...params}
                  inputLabel='Assigned Producer'
                  slotProps={{
                    inputLabel: { htmlFor: 'Assigned Producer' },
                    input: { ...params.InputProps },
                  }}
                  name='Assigned Producer'
                />
              )}
              renderOption={(props, option) =>
                (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
                  <li {...props} key={(option as Item)?.value}>
                    <Typography variant='bodySmall'>{(option as Item).label}</Typography>
                  </li>
                )
              }
              options={[...allProducers, { value: '', label: '' }]}
              value={currentProducer?.label}
              isOptionEqualToValue={(option, value) => option.label === (value as unknown as string)}
              sx={{ marginBottom: '24px' }}
              slotProps={{
                listbox: {
                  style: { height: 'fit-content', maxHeight: '280px' },
                  onScroll: (e) => handleScrollToBottom(e, loadMoreProducerResults),
                },
              }}
            />
            <UnderwriterTypeaheadFilter
              id='assigned-underwriter'
              value={currentUnderwriter?.value}
              disabled={!currentBrokerage?.value}
              label='Assigned Underwriter'
              onChange={(value) => {
                setCurrentUnderwriter(value);
              }}
            />
          </>
        )}
      </Grid>
    </Dialog>
  );
};
